import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertTriangle, Upload } from "lucide-react";

import type { ElementoImagem } from "@/types/etiqueta-designer";
import {
  bitmapDeElemento,
  desenharBitmapNoCanvas,
  dotsParaMm,
  elementoImagemDeBitmap,
  imagemParaBitmap,
  mmParaDots,
  type ContextoEtiqueta,
} from "@/utils/zplDesigner";

type Props = {
  aberto: boolean;
  onOpenChange: (v: boolean) => void;
  ctx: ContextoEtiqueta;
  /** quando presente, o diálogo reprocessa a imagem existente */
  elementoAtual?: ElementoImagem | null;
  onConfirmar: (
    dados: Pick<
      ElementoImagem,
      "larguraDots" | "alturaDots" | "bytesPorLinha" | "hex" | "arquivo"
    >,
  ) => void;
};

export function DialogoImagem({
  aberto,
  onOpenChange,
  ctx,
  elementoAtual,
  onConfirmar,
}: Props) {
  const [fonte, setFonte] = useState<CanvasImageSource | null>(null);
  const [dimensaoOriginal, setDimensaoOriginal] = useState({ w: 1, h: 1 });
  const [arquivo, setArquivo] = useState<string>("");
  const [larguraMm, setLarguraMm] = useState(20);
  const [alturaMm, setAlturaMm] = useState(20);
  const [manterProporcao, setManterProporcao] = useState(true);
  const [limiar, setLimiar] = useState(128);
  const [dither, setDither] = useState(false);
  const [inverter, setInverter] = useState(false);
  const previewRef = useRef<HTMLCanvasElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Semeia com a imagem já existente ao reabrir para reprocessar
  useEffect(() => {
    if (!aberto) return;
    if (!elementoAtual) {
      setFonte(null);
      setArquivo("");
      return;
    }
    const bmp = bitmapDeElemento(elementoAtual);
    const canvas = document.createElement("canvas");
    desenharBitmapNoCanvas(bmp, canvas);
    // canvas do bitmap tem fundo transparente; achata sobre branco
    const achatado = document.createElement("canvas");
    achatado.width = bmp.largura;
    achatado.height = bmp.altura;
    const ctx2d = achatado.getContext("2d");
    if (ctx2d) {
      ctx2d.fillStyle = "#fff";
      ctx2d.fillRect(0, 0, bmp.largura, bmp.altura);
      ctx2d.drawImage(canvas, 0, 0);
    }
    setFonte(achatado);
    setDimensaoOriginal({ w: bmp.largura, h: bmp.altura });
    setArquivo(elementoAtual.arquivo || "");
    setLarguraMm(dotsParaMm(elementoAtual.larguraDots, ctx.dpmm));
    setAlturaMm(dotsParaMm(elementoAtual.alturaDots, ctx.dpmm));
  }, [aberto, elementoAtual, ctx.dpmm]);

  const carregarArquivo = (file?: File | null) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setFonte(img);
      setDimensaoOriginal({ w: img.naturalWidth, h: img.naturalHeight });
      setArquivo(file.name);
      // encaixa em metade da largura da etiqueta, preservando proporção
      const larguraAlvo = Math.min(ctx.larguraMm * 0.5, 40);
      setLarguraMm(Math.round(larguraAlvo * 10) / 10);
      setAlturaMm(
        Math.round(
          (larguraAlvo * (img.naturalHeight / img.naturalWidth)) * 10,
        ) / 10,
      );
      URL.revokeObjectURL(url);
    };
    img.onerror = () => URL.revokeObjectURL(url);
    img.src = url;
  };

  const larguraDots = Math.max(1, mmParaDots(larguraMm, ctx.dpmm));
  const alturaDots = Math.max(1, mmParaDots(alturaMm, ctx.dpmm));

  const bitmap = useMemo(() => {
    if (!fonte) return null;
    return imagemParaBitmap(fonte, {
      larguraDots,
      alturaDots,
      limiar,
      dither,
      inverter,
    });
  }, [fonte, larguraDots, alturaDots, limiar, dither, inverter]);

  useEffect(() => {
    if (bitmap && previewRef.current)
      desenharBitmapNoCanvas(bitmap, previewRef.current);
  }, [bitmap]);

  const pesoKb = bitmap
    ? Math.round((bitmap.bytesPorLinha * bitmap.altura * 2) / 1024)
    : 0;
  const excedeLargura = larguraMm > ctx.larguraMm;
  const excedeAltura = alturaMm > ctx.alturaMm;

  const aplicarLargura = (v: number) => {
    setLarguraMm(v);
    if (manterProporcao && dimensaoOriginal.w) {
      setAlturaMm(
        Math.round(v * (dimensaoOriginal.h / dimensaoOriginal.w) * 10) / 10,
      );
    }
  };
  const aplicarAltura = (v: number) => {
    setAlturaMm(v);
    if (manterProporcao && dimensaoOriginal.h) {
      setLarguraMm(
        Math.round(v * (dimensaoOriginal.w / dimensaoOriginal.h) * 10) / 10,
      );
    }
  };

  return (
    <Dialog open={aberto} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {elementoAtual ? "Ajustar imagem" : "Adicionar imagem / logo"}
          </DialogTitle>
          <DialogDescription>
            A impressora só imprime preto e branco. Ajuste o limiar para
            controlar o que vira preto.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-3">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => carregarArquivo(e.target.files?.[0])}
            />
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-4 w-4" />
              {fonte ? "Trocar arquivo" : "Selecionar arquivo"}
            </Button>
            {arquivo && (
              <p className="truncate text-[11px] text-muted-foreground">
                {arquivo} · original {dimensaoOriginal.w}×{dimensaoOriginal.h}px
              </p>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div className="grid gap-1.5">
                <Label className="text-[11px] text-muted-foreground">
                  Largura (mm)
                </Label>
                <Input
                  type="number"
                  step="0.5"
                  min="1"
                  className="h-8 text-xs"
                  value={larguraMm}
                  onChange={(e) =>
                    aplicarLargura(parseFloat(e.target.value) || 0)
                  }
                />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-[11px] text-muted-foreground">
                  Altura (mm)
                </Label>
                <Input
                  type="number"
                  step="0.5"
                  min="1"
                  className="h-8 text-xs"
                  value={alturaMm}
                  onChange={(e) =>
                    aplicarAltura(parseFloat(e.target.value) || 0)
                  }
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="img-proporcao"
                checked={manterProporcao}
                onCheckedChange={setManterProporcao}
              />
              <Label htmlFor="img-proporcao" className="text-xs">
                Manter proporção
              </Label>
            </div>

            <div className="grid gap-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-[11px] text-muted-foreground">
                  Limiar de preto
                </Label>
                <span className="text-[11px] tabular-nums">{limiar}</span>
              </div>
              <Slider
                min={1}
                max={254}
                step={1}
                value={[limiar]}
                onValueChange={([v]) => setLimiar(v)}
              />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch id="img-dither" checked={dither} onCheckedChange={setDither} />
                <Label htmlFor="img-dither" className="text-xs">
                  Meio-tom
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="img-inverter"
                  checked={inverter}
                  onCheckedChange={setInverter}
                />
                <Label htmlFor="img-inverter" className="text-xs">
                  Inverter
                </Label>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-[11px] text-muted-foreground">
              Pré-visualização ({larguraDots}×{alturaDots} dots · ~{pesoKb} KB de
              ZPL)
            </Label>
            <div className="flex h-56 items-center justify-center overflow-auto rounded-md border bg-white p-2">
              {bitmap ? (
                <canvas
                  ref={previewRef}
                  style={{
                    imageRendering: "pixelated",
                    maxWidth: "100%",
                    maxHeight: "100%",
                  }}
                />
              ) : (
                <p className="text-xs text-muted-foreground">
                  Selecione uma imagem.
                </p>
              )}
            </div>
            {(excedeLargura || excedeAltura) && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-[11px] text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  A imagem é maior que a etiqueta ({ctx.larguraMm}×
                  {ctx.alturaMm} mm) e será cortada na impressão.
                </span>
              </div>
            )}
            {pesoKb > 200 && (
              <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2 text-[11px] text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  Imagem pesada ({pesoKb} KB). Reduza o tamanho para acelerar o
                  envio à impressora.
                </span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            size="sm"
            disabled={!bitmap}
            onClick={() => {
              if (!bitmap) return;
              onConfirmar({ ...elementoImagemDeBitmap(bitmap), arquivo });
              onOpenChange(false);
            }}
          >
            {elementoAtual ? "Aplicar" : "Adicionar imagem"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
